import { captureRequestScope } from '@/lib/api/api-client'
import { resolveOrganizationContext } from '@/lib/api/organization-context'
import { desktopAccountSession } from '@/lib/utils/native-credential-store'
import { useAuth } from '@/store/auth.store'
import { useOrganizationStore } from '@/store/organization.store'

import {
  createSessionScope,
  linkAbortSignals,
  SessionChangedError,
} from '@scrambl/shared/session-scope'

const accessScope = createSessionScope()
// Permission results must not be restored from an earlier browser session.
const runtimeId = crypto.randomUUID()
let initialized = false
let snapshot: ReturnType<typeof readSnapshot>

function readSnapshot() {
  const request = captureRequestScope()
  const user = useAuth.getState().user
  const selection = useOrganizationStore.getState()
  const organization = selection.currentOrganization
  const membership = organization?.userMembership
  const relationship = organization?.isExternalAccess
    ? selection.externalOrgRelationship
    : null
  const identity = JSON.stringify([
    runtimeId,
    request.identity,
    request.generation,
    user?.id,
    user?.role,
    organization?.id,
    membership?.id,
    membership?.role,
    membership?.status,
    relationship?.id,
    relationship?.hostOrganizationId,
    relationship?.status,
    relationship?.revokedAt,
    relationship?.permissionSet,
  ])
  accessScope.transition(identity)
  const access = accessScope.capture()
  let enabled = !!user?.id && !!organization?.id
  let organizationId: string | null = null
  try {
    organizationId =
      resolveOrganizationContext(selection, {}, true).orgId ?? null
  } catch {
    enabled = false
  }
  if (
    !organization?.isExternalAccess &&
    membership &&
    membership.status !== 'ACTIVE'
  )
    enabled = false
  const assertUnchanged = () => {
    request.assertCurrent()
    access.assertCurrent()
  }
  return {
    ownerUserId: user?.id ?? null,
    organizationId,
    signal: access.signal,
    assertUnchanged,
    key: [runtimeId, request.identity, request.generation, access.generation],
    enabled,
    async run<T>(
      signal: AbortSignal,
      fetch: (signal: AbortSignal) => Promise<T>,
    ): Promise<T> {
      const assertCurrent = () => {
        assertUnchanged()
        if (!enabled) throw new SessionChangedError()
      }
      assertCurrent()
      const linked = linkAbortSignals([signal, request.signal, access.signal])
      try {
        linked.signal.throwIfAborted()
        const result = await fetch(linked.signal)
        assertCurrent()
        linked.signal.throwIfAborted()
        return result
      } finally {
        linked.dispose()
      }
    },
  }
}

const listeners = new Set<() => void>()
function refresh() {
  const previous = accessScope.capture().generation
  const next = readSnapshot()
  if (snapshot && previous === accessScope.capture().generation) return
  snapshot = next
  listeners.forEach(listener => listener())
}
export function subscribeWorkspaceSession(listener: () => void) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function getWorkspaceSessionSnapshot() {
  // Defer store access until an explicit snapshot read: eager subscriptions
  // create an API/store initialization cycle. Retain them afterward to observe unmounted A -> B -> A.
  if (!initialized) {
    initialized = true
    refresh()
    useAuth.subscribe(refresh)
    useOrganizationStore.subscribe(refresh)
    desktopAccountSession.subscribe(refresh)
  }
  return snapshot
}
